/**
 * InputAdapter — Unified keyboard + touch input
 *
 * Provides:
 * - Keyboard state (pressed keys, just-pressed detection)
 * - Virtual joystick (touch, left half of screen)
 * - Touch fire (tap right half of screen)
 * - Mouse position tracking
 * - Normalized movement vector (-1 to 1 per axis)
 *
 * The virtual joystick uses a "relative" model: wherever you
 * first touch becomes the center, drag direction = movement.
 * This feels better than a fixed joystick position.
 */

export class InputAdapter {
  constructor(canvas) {
    this.canvas = canvas;

    // Keyboard state
    this._keys = {};
    this._justPressed = {};

    // Mouse
    this.mouseX = 0;
    this.mouseY = 0;
    this.mouseDown = false;

    // Virtual joystick
    this.joystick = {
      active: false,
      startX: 0,
      startY: 0,
      currentX: 0,
      currentY: 0,
      dx: 0, // normalized -1 to 1
      dy: 0,
      touchId: null,
    };

    // Fire touch
    this.fireTouching = false;
    this._fireTouchId = null;

    // Joystick config
    this._joystickDeadzone = 10; // pixels
    this._joystickMaxRadius = 60; // pixels

    // Bind handlers
    this._onKeyDown = this._onKeyDown.bind(this);
    this._onKeyUp = this._onKeyUp.bind(this);
    this._onMouseMove = this._onMouseMove.bind(this);
    this._onMouseDown = this._onMouseDown.bind(this);
    this._onMouseUp = this._onMouseUp.bind(this);
    this._onTouchStart = this._onTouchStart.bind(this);
    this._onTouchMove = this._onTouchMove.bind(this);
    this._onTouchEnd = this._onTouchEnd.bind(this);

    // Register listeners
    window.addEventListener('keydown', this._onKeyDown);
    window.addEventListener('keyup', this._onKeyUp);
    canvas.addEventListener('mousemove', this._onMouseMove);
    canvas.addEventListener('mousedown', this._onMouseDown);
    canvas.addEventListener('mouseup', this._onMouseUp);
    canvas.addEventListener('touchstart', this._onTouchStart, { passive: false });
    canvas.addEventListener('touchmove', this._onTouchMove, { passive: false });
    canvas.addEventListener('touchend', this._onTouchEnd, { passive: false });
    canvas.addEventListener('touchcancel', this._onTouchEnd, { passive: false });
  }

  // --- Public API ---

  /** Get normalized movement vector from keyboard + joystick */
  getMovement() {
    let mx = 0, my = 0;

    // Keyboard WASD + arrows
    if (this._keys['KeyW'] || this._keys['ArrowUp']) my -= 1;
    if (this._keys['KeyS'] || this._keys['ArrowDown']) my += 1;
    if (this._keys['KeyA'] || this._keys['ArrowLeft']) mx -= 1;
    if (this._keys['KeyD'] || this._keys['ArrowRight']) mx += 1;

    // Virtual joystick overrides (additive would cause too-fast movement)
    if (this.joystick.active) {
      mx = this.joystick.dx;
      my = this.joystick.dy;
    }

    // Normalize diagonal
    const len = Math.sqrt(mx * mx + my * my);
    if (len > 1) {
      mx /= len;
      my /= len;
    }

    return { x: mx, y: my };
  }

  /** Check if a key is currently held down */
  isDown(code) {
    return !!this._keys[code];
  }

  /** Check if a key was just pressed this frame (clear after check) */
  wasPressed(code) {
    if (this._justPressed[code]) {
      this._justPressed[code] = false;
      return true;
    }
    return false;
  }

  /** Is the player firing? (mouse click or right-half touch) */
  isFiring() {
    return this.mouseDown || this.fireTouching;
  }

  /** Clear per-frame state (call at end of update) */
  clearFrame() {
    this._justPressed = {};
  }

  /** Get joystick visual data for rendering the on-screen joystick */
  getJoystickVisual() {
    if (!this.joystick.active) return null;
    return {
      baseX: this.joystick.startX,
      baseY: this.joystick.startY,
      stickX: this.joystick.currentX,
      stickY: this.joystick.currentY,
      radius: this._joystickMaxRadius,
    };
  }

  // --- Keyboard Handlers ---

  _onKeyDown(e) {
    if (!this._keys[e.code]) {
      this._justPressed[e.code] = true;
    }
    this._keys[e.code] = true;

    // Prevent arrow keys from scrolling
    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Space'].includes(e.code)) {
      e.preventDefault();
    }
  }

  _onKeyUp(e) {
    this._keys[e.code] = false;
  }

  // --- Mouse Handlers ---

  _onMouseMove(e) {
    const rect = this.canvas.getBoundingClientRect();
    this.mouseX = e.clientX - rect.left;
    this.mouseY = e.clientY - rect.top;
  }

  _onMouseDown(e) {
    this.mouseDown = true;
    this._onMouseMove(e);
  }

  _onMouseUp() {
    this.mouseDown = false;
  }

  // --- Touch Handlers ---

  _onTouchStart(e) {
    e.preventDefault();
    const rect = this.canvas.getBoundingClientRect();
    const halfW = rect.width / 2;

    for (const touch of e.changedTouches) {
      const x = touch.clientX - rect.left;
      const y = touch.clientY - rect.top;

      if (x < halfW && this.joystick.touchId === null) {
        // Left half — joystick
        this.joystick.active = true;
        this.joystick.touchId = touch.identifier;
        this.joystick.startX = x;
        this.joystick.startY = y;
        this.joystick.currentX = x;
        this.joystick.currentY = y;
        this.joystick.dx = 0;
        this.joystick.dy = 0;
      } else if (x >= halfW && this._fireTouchId === null) {
        // Right half — fire
        this.fireTouching = true;
        this._fireTouchId = touch.identifier;
        this.mouseX = x;
        this.mouseY = y;
      }
    }
  }

  _onTouchMove(e) {
    e.preventDefault();
    const rect = this.canvas.getBoundingClientRect();

    for (const touch of e.changedTouches) {
      const x = touch.clientX - rect.left;
      const y = touch.clientY - rect.top;

      if (touch.identifier === this.joystick.touchId) {
        this.joystick.currentX = x;
        this.joystick.currentY = y;

        let dx = x - this.joystick.startX;
        let dy = y - this.joystick.startY;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist < this._joystickDeadzone) {
          this.joystick.dx = 0;
          this.joystick.dy = 0;
        } else {
          // Clamp to max radius
          const clampedDist = Math.min(dist, this._joystickMaxRadius);
          this.joystick.dx = (dx / dist) * (clampedDist / this._joystickMaxRadius);
          this.joystick.dy = (dy / dist) * (clampedDist / this._joystickMaxRadius);
        }
      } else if (touch.identifier === this._fireTouchId) {
        this.mouseX = x;
        this.mouseY = y;
      }
    }
  }

  _onTouchEnd(e) {
    for (const touch of e.changedTouches) {
      if (touch.identifier === this.joystick.touchId) {
        this.joystick.active = false;
        this.joystick.touchId = null;
        this.joystick.dx = 0;
        this.joystick.dy = 0;
      }
      if (touch.identifier === this._fireTouchId) {
        this.fireTouching = false;
        this._fireTouchId = null;
      }
    }
  }

  // --- Cleanup ---

  destroy() {
    window.removeEventListener('keydown', this._onKeyDown);
    window.removeEventListener('keyup', this._onKeyUp);
    this.canvas.removeEventListener('mousemove', this._onMouseMove);
    this.canvas.removeEventListener('mousedown', this._onMouseDown);
    this.canvas.removeEventListener('mouseup', this._onMouseUp);
    this.canvas.removeEventListener('touchstart', this._onTouchStart);
    this.canvas.removeEventListener('touchmove', this._onTouchMove);
    this.canvas.removeEventListener('touchend', this._onTouchEnd);
    this.canvas.removeEventListener('touchcancel', this._onTouchEnd);
  }
}
